import React from "react"
import { twMerge } from "tailwind-merge"

import { sanitize } from "../../../utils/utils"
import { getSectionColorScheme } from "../utils/colorModeUtils"
import {
  formatFontFamily,
  formatHeadingFontFamily,
  getParagraphFontWeight,
  stripWysiwygInlineStylesFromHtml
} from "../utils/fontUtils"
import { applyButtonTheme, getButtonClasses, getThemeShapeClasses } from "../utils/themeUtils"
import { buildServiceUrl, buildBookingUrl, buildPublicPageUrl, openExternalUrl } from "../utils/urlUtils"
import { getWysiwygScopeId, getWysiwygScopeSelector } from "../utils/wysiwygScopeUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

import { ScrollRevealItem } from "./shared/ScrollReveal"

const AboutSectionMobile = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}

  // Get color scheme based on color mode
  const colorScheme = getSectionColorScheme(settings, theme)
  const { themeColors } = colorScheme

  const layout = settings.layout || "image_left"
  const showCTA = settings.showCTA !== undefined ? settings.showCTA : true
  const ctaText = settings.ctaText || "Learn more"
  const ctaAction = settings.ctaAction || "page"
  const ctaTarget = settings.ctaTarget || ""

  // Use color scheme for colors
  const headingColor = colorScheme.headingColor
  const bodyColor = colorScheme.bodyColor
  const sectionBackgroundColor = colorScheme.sectionBackground
  const linkColor = colorScheme.linkColor
  const buttonStyles = colorScheme.primaryButton
  const wysiwygScope = getWysiwygScopeSelector(section.id, "about")
  const wysiwygScopeId = getWysiwygScopeId(section.id, "about")

  const resolveWysiwygHtml = (value) => {
    if (!value) return ""

    if (typeof value === "object") {
      return value.html || ""
    }

    if (typeof value === "string") {
      const trimmed = value.trim()
      if (!trimmed) return ""

      if (trimmed.startsWith("{") && trimmed.includes('"html"')) {
        try {
          const parsed = JSON.parse(trimmed)
          if (parsed && typeof parsed === "object" && typeof parsed.html === "string") {
            return parsed.html
          }
        } catch (_) {
          // Not JSON
        }
      }

      if (!trimmed.includes("<")) {
        return trimmed.replace(/\n/g, "<br />")
      }

      return value
    }

    return String(value)
  }

  const bodyHtml = sanitize(stripWysiwygInlineStylesFromHtml(resolveWysiwygHtml(section.body)))

  const handleCTAClick = () => {
    switch (ctaAction) {
      case "page":
        if (ctaTarget) {
          window.location.href = buildPublicPageUrl(ctaTarget, practice)
        }
        break
      case "service":
        if (ctaTarget) {
          window.location.href = buildServiceUrl(ctaTarget, practice)
        }
        break
      case "booking":
        window.location.href = buildBookingUrl(practice)
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        openExternalUrl(ctaTarget)
        break
      default:
        break
    }
  }

  const renderContent = () => (
    <div className="flex w-full max-w-full flex-col gap-6">
      {/* Text Content */}
      <div className="flex w-full flex-col gap-6">
        {section.headline && (
          <ScrollRevealItem staggerIndex={0}>
            <h2
              className="w-full text-[24px] font-bold leading-[30px]"
              style={{
                fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: headingColor,
                wordBreak: "normal",
                overflowWrap: "normal",
                hyphens: "none"
              }}>
              {section.headline}
            </h2>
          </ScrollRevealItem>
        )}
        {section.body && (
          <ScrollRevealItem staggerIndex={1}>
            <>
              <style>{`
              ${wysiwygScope} {
                text-align: initial !important;
                word-break: normal !important;
                overflow-wrap: normal !important;
                hyphens: none !important;
                font-family: ${formatFontFamily(theme?.fonts?.paragraph)} !important;
                font-weight: ${getParagraphFontWeight(
                  theme?.fonts?.paragraph,
                  theme?.fonts?.paragraphRegularWeight
                )} !important;
              }
              ${wysiwygScope} p,
              ${wysiwygScope} li,
              ${wysiwygScope} span,
              ${wysiwygScope} div:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6),
              ${wysiwygScope} *:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6):not(strong):not(b):not(em):not(i):not(u) {
                font-family: ${formatFontFamily(theme?.fonts?.paragraph)} !important;
                font-weight: ${getParagraphFontWeight(
                  theme?.fonts?.paragraph,
                  theme?.fonts?.paragraphRegularWeight
                )} !important;
              }
              ${wysiwygScope} ul {
                list-style-type: disc !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              ${wysiwygScope} ol {
                list-style-type: decimal !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              ${wysiwygScope} li {
                margin-bottom: 0.5rem !important;
                display: list-item !important;
              }
              ${wysiwygScope} .ql-align-right {
                text-align: right !important;
              }
              ${wysiwygScope} .ql-align-center {
                text-align: center !important;
              }
              ${wysiwygScope} .ql-align-justify {
                text-align: justify !important;
              }
              ${wysiwygScope} .ql-align-left {
                text-align: left !important;
              }
              ${wysiwygScope} a {
                color: ${linkColor} !important;
                text-decoration: underline !important;
              }
              ${wysiwygScope} a:hover {
                opacity: 0.8 !important;
                text-decoration: underline !important;
              }
              ${wysiwygScope} p {
                margin-bottom: 1rem !important;
                margin-top: 0 !important;
              }
              ${wysiwygScope} p:first-child {
                margin-top: 0 !important;
              }
              ${wysiwygScope} p:last-child {
                margin-bottom: 0 !important;
              }
              ${wysiwygScope} strong,
              ${wysiwygScope} b {
                font-weight: ${theme?.fonts?.paragraphBoldWeight || "600"} !important;
                color: inherit !important;
              }
              ${wysiwygScope} em,
              ${wysiwygScope} i {
                font-style: italic !important;
              }
              ${wysiwygScope} u {
                text-decoration: underline !important;
              }
              ${wysiwygScope} h1,
              ${wysiwygScope} h2,
              ${wysiwygScope} h3,
              ${wysiwygScope} h4,
              ${wysiwygScope} h5,
              ${wysiwygScope} h6 {
                font-family: ${formatHeadingFontFamily(theme?.fonts?.heading)} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                color: ${bodyColor} !important;
              }
              ${wysiwygScope} h1 *,
              ${wysiwygScope} h2 *,
              ${wysiwygScope} h3 *,
              ${wysiwygScope} h4 *,
              ${wysiwygScope} h5 *,
              ${wysiwygScope} h6 * {
                color: inherit !important;
              }
              ${wysiwygScope} h1 {
                font-size: 26px !important;
                line-height: 32px !important;
                overflow-wrap: normal !important;
                word-break: normal !important;
                hyphens: none !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
              }
              ${wysiwygScope} h2 {
                font-size: 28px !important;
                line-height: 32px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
              }
              ${wysiwygScope} h3 {
                font-size: 24px !important;
                line-height: 28px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
              }
              ${wysiwygScope} h4 {
                font-size: 18px !important;
                line-height: 24px !important;
                margin-bottom: 0.75rem !important;
                margin-top: 1.25rem !important;
              }
              ${wysiwygScope} h5 {
                font-size: 16px !important;
                line-height: 24px !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
              }
              ${wysiwygScope} h6 {
                font-size: 14px !important;
                line-height: 20px !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
              }
            `}</style>
              <div
                id={wysiwygScopeId}
                className="about-section-content wysiwyg-content w-full max-w-none text-[16px] leading-[22px]" // eslint-disable-line tailwindcss/no-custom-classname
                style={{
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  fontWeight: getParagraphFontWeight(theme?.fonts?.paragraph, theme?.fonts?.paragraphRegularWeight),
                  color: bodyColor
                }}
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />
            </>
          </ScrollRevealItem>
        )}
      </div>

      {showCTA && (
        <ScrollRevealItem staggerIndex={2}>
          <button
            onClick={handleCTAClick}
            className={twMerge(
              "inline-flex items-center justify-center px-10 py-3 text-[16px] font-normal leading-[24px] transition-all hover:shadow-lg",
              getButtonClasses(theme?.buttonStyle)
            )}
            style={applyButtonTheme(
              {
                backgroundColor: buttonStyles.backgroundColor,
                color: buttonStyles.color,
                fontFamily: formatFontFamily(theme?.fonts?.paragraph)
              },
              theme
            )}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = buttonStyles.hoverBackgroundColor
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = buttonStyles.backgroundColor
            }}>
            {ctaText}
          </button>
        </ScrollRevealItem>
      )}
    </div>
  )

  // Mobile-specific layouts
  switch (layout) {
    case "image_left":
    case "image_right":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: sectionBackgroundColor }}>
          <div className="mx-auto w-full px-4 py-8">
            <div className="flex w-full flex-col items-center gap-5">
              {/* Mobile Image */}
              {section.imageUrl ? (
                <div className="relative w-full">
                  <div
                    className={twMerge("h-[320px] w-full bg-cover bg-center", getThemeShapeClasses(theme))}
                    style={{
                      backgroundImage: `url(${section.imageUrl})`,
                      backgroundColor: `${themeColors.bgMedium || "#E6DAD7"} !important`
                    }}
                  />
                </div>
              ) : (
                <div className="relative w-full">
                  <div
                    className={twMerge(
                      "flex h-[320px] w-full items-center justify-center bg-gray-200",
                      getThemeShapeClasses(theme)
                    )}>
                    <span className="text-gray-500">No image selected</span>
                  </div>
                </div>
              )}
              <div className="w-full flex-1">{renderContent()}</div>
            </div>
          </div>
        </section>
      )

    case "centered":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[400px] items-center justify-center py-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: sectionBackgroundColor }}>
          <div className="mx-auto w-full px-4 text-center">
            {section.imageUrl && (
              <div className="mb-6 flex justify-center">
                <div className="relative">
                  <div
                    className={twMerge("absolute -bottom-4 -right-4 h-16 w-12", getThemeShapeClasses(theme))}
                    style={{ backgroundColor: theme?.colors?.bgMedium || "#E6DAD7" }}
                  />
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "About image"}
                    className={twMerge("relative size-48 object-cover", getThemeShapeClasses(theme))}
                  />
                </div>
              </div>
            )}
            {renderContent()}
          </div>
        </section>
      )

    case "no_image":
    default:
      return (
        <section
          className={twMerge(
            "relative flex min-h-[400px] items-center justify-center py-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: sectionBackgroundColor }}>
          <div className="mx-auto w-full px-4">{renderContent()}</div>
        </section>
      )
  }
}

export default AboutSectionMobile
